<template>
    <div class="shift-editor">
        <div class="shift-editor-body">
            <!-- Закрытие — общая кнопка SidePanel справа сверху. -->
            <div class="shift-editor-title">{{ dateTitle }}</div>

            <div class="shift-editor-person">
                <div class="shift-editor-employee">
                    <Avatar :first-name="schedule.user.first_name" :last-name="schedule.user.last_name" size="sm" width="32" height="32" :url="schedule.user.avatar" />
                    <div class="min-w-0">
                        <div class="shift-editor-name">{{ schedule.user.last_name }} {{ schedule.user.first_name }} {{ schedule.user.middle_name }}</div>
                        <div class="shift-editor-meta">{{ [schedule.position?.name, schedule.department.name].filter(Boolean).join(' · ') }}</div>
                    </div>
                </div>
                <ShiftMonthSummary :schedule />
            </div>

            <!-- Во время preview/execute форма и карточки недоступны: подтверждаемый контекст не меняется. -->
            <div :inert="isCopyLoading" :class="{ 'shift-editor-locked': isCopyLoading }">
                <Shifts ref="shiftsRef" :schedule :date="dt" @switch="emit('switch')" />
            </div>

            <div v-if="activeTab" class="shift-editor-tabs-block" :class="{ 'shift-editor-tabs-block-fill': activeTab === 'history' }">
                <div role="tablist" aria-label="Копирование и история" class="shift-editor-tabs">
                    <button
                        v-if="isCopyTabVisible"
                        type="button"
                        role="tab"
                        class="shift-editor-tab"
                        :class="{ 'shift-editor-tab-active': activeTab === 'copy' }"
                        :aria-selected="activeTab === 'copy'"
                        @click="selectTab('copy')"
                    >Копирование</button>
                    <button
                        v-if="showShiftHistory"
                        type="button"
                        role="tab"
                        :disabled="isCopyLoading"
                        class="shift-editor-tab"
                        :class="{ 'shift-editor-tab-active': activeTab === 'history' }"
                        :aria-selected="activeTab === 'history'"
                        @click="selectTab('history')"
                    >История</button>
                </div>

                <!-- История прокручивается внутри, в пределах видимой части панели. -->
                <div v-if="activeTab === 'history'" class="shift-editor-history-scroll">
                    <ShiftHistory />
                </div>
                <!-- Несохранённые правки дня: копирование временно недоступно, вкладка не скрывается. -->
                <div v-else-if="activeTab === 'copy' && !isCopyAvailable" class="shift-editor-tab-note">
                    Сначала сохраните или отмените изменения смен
                </div>
                <ShiftsCopy
                    v-else-if="activeTab === 'copy'"
                    v-model="copyTargetDates"
                    :dt
                    :source-shifts="savedShifts"
                    :mode="copyMode"
                    :errors="copyProcess.modalErrors.value"
                    :preview="copyProcess.preview.value"
                    :is-loading="copyProcess.isLoading.value"
                />
            </div>
        </div>

        <!-- Подтверждение замены — в футере вкладки, без модалки. -->
        <div v-if="copyMode === 'confirm'" class="shift-editor-footer shift-editor-footer-confirm">
            <span class="shift-editor-footer-note">{{ deleteConfirmText }}</span>
            <button type="button" class="shift-editor-button shift-editor-button-light" :disabled="isCopyLoading" @click="copyProcess.close">Назад</button>
            <button type="button" class="shift-editor-button shift-editor-button-primary" :disabled="isCopyLoading" @click="copyProcess.confirm">Заменить и скопировать</button>
        </div>
        <div v-else-if="isCopyFooter || isCopyLoading" class="shift-editor-footer">
            <button type="button" class="shift-editor-button shift-editor-button-secondary" :disabled="isCopyLoading" @click="emit('close')">Отмена</button>
            <button type="button" class="shift-editor-button shift-editor-button-primary" :disabled="isCopyLoading" @click="startCopy">
                Скопировать в {{ copyDaysCount }} {{ pluralRu(copyDaysCount, ['день', 'дня', 'дней']) }}
            </button>
        </div>
        <div v-else class="shift-editor-footer">
            <button v-if="canDeleteCurrentShift" type="button" class="shift-editor-delete" @click="deleteCurrentShift">
                <icon width="14" height="14" :vb-size="20" class="shift-editor-delete-icon"><IconTrash /></icon>Удалить смену
            </button>
            <button type="button" class="shift-editor-button shift-editor-button-secondary" @click="emit('close')">Отмена</button>
            <button type="button" class="shift-editor-button shift-editor-button-primary" :disabled="!store.isSaveShiftEnabled" @click="saveShifts()">Сохранить</button>
        </div>
    </div>
</template>

<script setup lang="ts">
interface Props {
    scheduleIndex: number,
    day: number,
    // Точная дата ячейки (недельный вид может охватывать два месяца); без неё — день текущего месяца.
    date?: string
}
import { useScheduleStore } from '@/stores/scheduleStore'
import { onMounted, computed, onUnmounted, ref, watch } from 'vue'
import Avatar from '../Avatar.vue'
import dayjs from "dayjs"
import Shifts from './Shifts.vue'
import ShiftsCopy from './ShiftsCopy.vue'
import ShiftHistory from './ShiftHistory.vue'
import ShiftMonthSummary from './ShiftMonthSummary.vue'
import IconTrash from '@/ui/icons/IconTrash.vue'
import { useAuthStore } from '@/stores/authStore'
import { formatShiftsCount, pluralRu, useShiftCopy } from './useShiftCopy'
import { currentCopyHighlightContext, highlightCopiedCells } from './copyHighlight'
import type { ShiftCopyExecuteResponse, ShiftCopyRequest } from '@/api/schedule'
import { ownRowShifts } from '@/utils/otherDepartmentShifts'

const emit = defineEmits(['close', 'switch'])

const store = useScheduleStore()
const authStore = useAuthStore()
const shiftsRef = ref()
const copyTargetDates = ref<string[]>([])
const copyRequestDays = ref(0)
// Панель размонтирована — колбэки отправленной операции не трогают повторно открытый редактор.
let isEditorMounted = true
// Фоновое обновление, как у копирования в сетке: без Loader и перерисовки всей таблицы.
const copyProcess = useShiftCopy({ backgroundRefresh: true })

dayjs.locale('ru')

const { scheduleIndex, day, date } = defineProps<Props>()

const dt = computed(() => date ?? dayjs(store.currentScheduleDate).date(day).format('YYYY-MM-DD'))
const dateTitle = computed(() => {
    const title = dayjs(dt.value).format('dd, D MMMM YYYY')
    return title.charAt(0).toUpperCase() + title.slice(1)
})

const schedule = computed(() => store.schedules[scheduleIndex])

// Смены других подразделений и других ТУ в боковую панель не попадают: они не редактируются из этой строки (RD-182).
const shifts = computed(() => ownRowShifts(schedule.value, store.currentDepartment?.id)
    .filter(shift => dayjs(shift.date).isSame(dt.value, 'day')))
const savedShifts = computed(() => shifts.value.filter(item => item.id))
// Смена уходит из графика сразу после DELETE, а выбор меняется только после проверки правил:
// в этом промежутке удалённая смена уже не считается сохранённой (её истории на сервере нет).
const isCurrentShiftSaved = computed(() => savedShifts.value.some(item => item.id === store.currentShift?.id))

// История — по выбранной сохранённой смене: у пустого дня и новой смены вкладки нет.
const showShiftHistory = computed(() => Boolean(authStore.permissions?.can_view_shift_history && isCurrentShiftSaved.value))

const hasDayStateDocumentInCurrentDay = computed(() => store.currentShifts.some((shift) => Boolean(shift?.day_state_document)))
const isCurrentDayDirty = computed(() => Boolean(store.changedShifts.length || store.shiftFieldsClicked || store.currentShifts.some(shift => !shift?.id)))
// Вкладка копирования есть только у дня, который можно копировать: право, будущий день,
// сохранённые смены, без кадрового состояния. Иначе вкладка не показывается.
const isCopyTabVisible = computed(() => Boolean(
    authStore.permissions?.can_create_schedule
    && dayjs(dt.value).isAfter(dayjs(), 'day')
    && savedShifts.value.length
    && !hasDayStateDocumentInCurrentDay.value
))
// Несохранённые правки — временная блокировка: вкладка остаётся, вместо календаря подсказка.
const isCopyAvailable = computed(() => isCopyTabVisible.value && !isCurrentDayDirty.value)

// Копирование — первая вкладка и открывается по умолчанию: история (отдельный запрос)
// загружается только по выбору вкладки или если копирования у дня нет.
type EditorTab = 'history' | 'copy'
const selectedTab = ref<EditorTab>(isCopyTabVisible.value ? 'copy' : 'history')
const activeTab = computed<EditorTab | null>(() => {
    if (selectedTab.value === 'copy' && isCopyTabVisible.value) return 'copy'
    if (showShiftHistory.value) return 'history'
    if (isCopyTabVisible.value) return 'copy'
    return null
})
const selectTab = (tab: EditorTab) => {
    if (isCopyLoading.value) return
    selectedTab.value = tab
}

const isCopyLoading = computed(() => copyProcess.isLoading.value)
const copyMode = computed<'select' | 'confirm' | 'errors'>(() => {
    if (!copyProcess.isModalOpen.value) return 'select'
    return copyProcess.modalMode.value
})
// Во время запроса — даты отправленного payload, а не текущий выбор.
const copyDaysCount = computed(() => (isCopyLoading.value && copyRequestDays.value) || copyTargetDates.value.length)
const isCopyFooter = computed(() => activeTab.value === 'copy' && copyTargetDates.value.length > 0)
const deleteConfirmText = computed(() => {
    const count = copyProcess.preview.value?.total_shifts_to_delete ?? 0
    return `${formatShiftsCount(count)} ${pluralRu(count, ['будет удалена', 'будут удалены', 'будут удалены'])}`
})

// Удаление — прежний путь Shifts.onShiftDelete; у КС удаление недоступно, как и раньше.
const canDeleteCurrentShift = computed(() => Boolean(isCurrentShiftSaved.value && !store.currentShift?.day_state_document))
const deleteCurrentShift = () => shiftsRef.value?.deleteCurrentShift()

const copySuccessMessage = (result: NonNullable<ShiftCopyExecuteResponse['result']>, payload: ShiftCopyRequest) => {
    const created = result.shifts_created
    const deleted = result.shifts_deleted
    const days = payload.target_dates.length
    const verb = pluralRu(created, ['Скопирована', 'Скопированы', 'Скопировано'])
    const replaced = deleted > 0 ? `, ${deleted} ${pluralRu(deleted, ['заменена', 'заменены', 'заменено'])}` : ''
    return `${verb} ${formatShiftsCount(created)} в ${days} ${pluralRu(days, ['день', 'дня', 'дней'])}${replaced}`
}

// Подсвечиваются только цели ТУ, успешно скопированные по фактическому result.
// Контекст сетки на момент старта копирования: уход со страницы/смена периода до завершения отменяют подсветку.
let copyHighlightContext = 0
const onCopyCompleted = (result: NonNullable<ShiftCopyExecuteResponse['result']>, payload: ShiftCopyRequest) => {
    const employmentId = payload.employment_ids[0]
    const isSucceeded = result.employments.some(item => item.employment_id === employmentId && item.success)
    if (isSucceeded) highlightCopiedCells(employmentId, payload.target_dates, copyHighlightContext)
}

async function startCopy() {
    if (isCopyLoading.value || !copyTargetDates.value.length) return
    if (!store.currentDepartment?.id || !store.currentShift?.employment) return
    copyRequestDays.value = copyTargetDates.value.length
    copyHighlightContext = currentCopyHighlightContext()
    await copyProcess.start({
        department_id: store.currentDepartment.id,
        employment_ids: [store.currentShift.employment],
        source_dates: [dt.value],
        target_dates: [...copyTargetDates.value],
    }, () => {
        if (!isEditorMounted) return
        copyTargetDates.value = []
        emit('close')
    }, { successMessage: copySuccessMessage, onCompleted: onCopyCompleted })
}

// Один незавершённый submit: кнопка может снова включиться от правок во время запроса.
let isSavingShifts = false

async function saveShifts() {
    if (isSavingShifts) return
    if (!validateCurrentShiftPlanTime()) return

    const savingShifts = [store.currentShift]
    store.isSaveShiftEnabled = false
    isSavingShifts = true
    let savedShifts: Awaited<ReturnType<typeof store.shiftPackCreateUpdate>>
    try {
        savedShifts = await store.shiftPackCreateUpdate(savingShifts)
    } finally {
        isSavingShifts = false
    }
    // Отказ записи: черновик и dirty остаются, панель открыта, повтор доступен.
    if (!savedShifts) {
        if (isEditorMounted) await store.handleSaveButtonAvailability()
        return
    }
    store.isCurrentShiftChanged = false
    emit('close')
}

watch(isCopyAvailable, (isVisible) => {
    // Во время запроса выбор не меняется (форма заблокирована); после — прежний сброс.
    if (!isVisible && !isCopyLoading.value) copyTargetDates.value = []
})

// Новый выбор дат делает прежние preview/ошибки неприменимыми (во время запроса выбор заблокирован).
watch(copyTargetDates, () => {
    if (copyProcess.isModalOpen.value && copyProcess.modalMode.value === 'confirm' && !isCopyLoading.value) copyProcess.close()
})

onMounted(() => {
    const originalShifts = JSON.parse(JSON.stringify(shifts.value))
    store.setCurrentShifts([...shifts.value])
    store.setOriginalShifts(originalShifts)
    if (shifts.value.length) {
        store.setCurrentShift({...shifts.value[0]})
    }
})

onUnmounted(() => {
    isEditorMounted = false
    store.setCurrentShifts([])
    store.setCurrentShift(null)
    store.changedShifts = []
    store.shiftFieldsClicked = false
})

const completePendingShiftSwitch = () => {
    shiftsRef.value?.completePendingSwitch()
}

const cancelPendingShiftSwitch = () => {
    shiftsRef.value?.cancelPendingSwitch()
}

const validateCurrentShiftPlanTime = () => {
    return shiftsRef.value?.validateCurrentShiftPlanTime() ?? true
}

defineExpose({
    // Владелец блокирует закрытие панели (SidePanel locked), пока идёт копирование.
    isBusy: isCopyLoading,
    completePendingShiftSwitch,
    cancelPendingShiftSwitch,
    validateCurrentShiftPlanTime
})
</script>

<style scoped>
.shift-editor {
    display: flex;
    flex-direction: column;
    height: 100vh;
    color: var(--color-text-main);
}

.shift-editor-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 16px 20px 20px;
    overflow-y: auto;
}

.shift-editor-title {
    padding-right: 32px;
    font-size: 18px;
    font-weight: 500;
    line-height: 26px;
}

.shift-editor-person {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.shift-editor-employee {
    display: flex;
    align-items: center;
    gap: 10px;
}

.shift-editor-name {
    font-size: 13px;
    font-weight: 600;
}

.shift-editor-meta {
    font-size: 12px;
    color: var(--color-text-muted);
}

.shift-editor-body > * {
    flex: none;
}

.shift-editor-locked {
    opacity: .6;
}

.shift-editor-tabs-block {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

/* История занимает оставшуюся высоту панели; если форма выше экрана — не меньше 160 px,
   и тогда прокручивается вся панель, как раньше. */
.shift-editor-body > .shift-editor-tabs-block-fill {
    flex: 1 1 0;
    min-height: 160px;
}

.shift-editor-history-scroll {
    flex: 1 1 0;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
}

.shift-editor-tabs {
    display: flex;
    gap: 20px;
    border-bottom: 1px solid var(--color-bg-muted);
}

.shift-editor-tab {
    height: 30px;
    padding: 0;
    border: 0;
    border-bottom: 2px solid transparent;
    background: transparent;
    font-size: 13px;
    color: var(--color-text-muted);
    cursor: pointer;
}

.shift-editor-tab-active {
    border-bottom-color: var(--color-brand-main);
    font-weight: 600;
    color: var(--color-brand-main);
}

.shift-editor-tab-note {
    font-size: 12px;
    color: var(--color-text-muted);
}

.shift-editor-tab:focus-visible,
.shift-editor-button:focus-visible,
.shift-editor-delete:focus-visible {
    outline: 2px solid var(--color-focus-border);
    outline-offset: 2px;
}

.shift-editor-footer {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid var(--color-bg-muted);
}

.shift-editor-footer-confirm {
    background-color: var(--color-warning-bg);
}

.shift-editor-footer-note {
    margin-right: auto;
    font-size: 12px;
    font-weight: 500;
}

.shift-editor-button {
    padding: 9px 10px;
    border: 0;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
}

.shift-editor-button:disabled {
    opacity: .5;
    cursor: not-allowed;
}

.shift-editor-button-primary {
    background-color: var(--color-brand-main);
    color: var(--color-white);
}

.shift-editor-button-secondary {
    background-color: var(--color-bg-muted);
    color: var(--color-text-main);
}

.shift-editor-button-light {
    background-color: var(--color-white);
    color: var(--color-text-main);
}

.shift-editor-delete {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-right: auto;
    padding: 9px 6px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    font-size: 12px;
    font-weight: 500;
    color: var(--color-text-main);
    cursor: pointer;
}

.shift-editor-delete-icon {
    color: var(--color-danger);
}
</style>
